import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
} from "@mui/material";
import { FormikProps, useFormik } from "formik";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import SpinnerArea from "../../../../components/SpinnerArea";
import MainContext from "../../../../context/mainContext";
import useService from "../../../../hooks/useService";
import { DraftForFeedbackModel } from "../../../../models/workflow.model";
import workflowService from "../../../../services/workflow.service";
import WorkflowContext from "../../context/WorkflowContext";
import * as Yup from "yup";
import styles from "./DraftForFeedback.module.scss";
import { PeoplePickerMultiple } from "../../../../components/SelectPicker/PeoplePickers";
import SaveSuccessDialog from "../../SaveSuccessDialog";
import CancelDialog from "../../CancelDialog";
import FileUploader, { FileWrapper } from "../../../../components/FileUploader";
import DocumentView from "../../../../components/DocumentView";
import IconTooltip from "../../../../components/IconTooltip";
import { validateBeforeExecute } from "../../../../misc/utils.functions";

const _emptyDraft: DraftForFeedbackModel = {
  mandatoryReview: true,
  dontNeedReview: false,
  reviewers: [],
  isCompleted: false,
  canEdit: true,
};

const DraftForFeedback: React.FC = () => {
  const { ticketNumber } = useParams();
  const { service } = useContext(MainContext);
  const { currentWorkflow, reload } = useContext(WorkflowContext);
  const { t } = useTranslation();
  const [model, loading] = useService(() =>
    workflowService.draftForFeedback(ticketNumber!)
  );
  const [isReadOnly, setIsReadOnly] = useState(false);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [cancelDialogShow, cancelDialogShowSet] = useState(false);
  const [uploadDocument, setUploadDocument] = useState<File | null>();
  const [requiredDocumentError, setRequiredDocumentError] = useState<
    boolean | null
  >(null);

  const validationSchema = Yup.object().shape({
    reviewers: Yup.array().when("dontNeedReview", {
      is: true,
      then: Yup.array()
        .optional()
        .max(3, t("maxAllowedUsers", { max: "3" })!),
      otherwise: Yup.array()
        .min(isReadOnly ? 0 : 1, t("requiredField")!)
        .max(3, t("maxAllowedUsers", { max: "3" })!),
    }),
  });

  const formik = useFormik<DraftForFeedbackModel>({
    initialValues: model || _emptyDraft,
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const handleContinueCondition = (): boolean => {
    if (!model?.draft && !uploadDocument) {
      setRequiredDocumentError(true);
      return false;
    }

    return true;
  };

  const save = (values: DraftForFeedbackModel, nextStep: boolean) => {
    service(
      () =>
        workflowService.draftForFeedbackSave(
          ticketNumber!,
          values,
          nextStep,
          uploadDocument
        ),
      {
        callback: (response) => {
          if (!nextStep) setSaveSuccess(true);
          else {
            reload();
            navigate(
              `/workflow/${response.ticketNumber}/${response.nextStep}`,
              { relative: "path" }
            );
          }
        },
        loader: setSaving,
      }
    );
  };

  const cancel = (formik: FormikProps<DraftForFeedbackModel>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  useEffect(() => {
    if (model) {
      setIsReadOnly(model.isCompleted || !model.canEdit);
    }
  }, [model]);

  return (
    <SpinnerArea loading={loading || saving}>
      <form
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
        className={styles.draftForFeedbackForm}
      >
        <Grid container>
          <Grid item sm={12}>
            <div className={styles.header}>
              <p>
                <strong>{t("publicationTicketNumber")}: </strong>
                {currentWorkflow?.ticketNumber}
              </p>
              <p className={styles.aclaration}>
                <strong>{t("uploadDraftForFeedback")} </strong>
                {t("draftForFeedbackAclaration")}
              </p>
              <p className={styles.message}>{t("draftForFeedbackMessage")}</p>
            </div>
          </Grid>
        </Grid>
        <div className={styles.formContainer}>
          <div className={styles["item-1"]}>
            <FormGroup>
              <FormControl fullWidth>
                <FormLabel id="reviewers-label">
                  {model
                    ? t(
                        model?.mandatoryReview
                          ? "addPeerReviewers"
                          : "addColleguesForFeedback"
                      )
                    : ""}
                </FormLabel>
                <PeoplePickerMultiple
                  id="reviewers"
                  defaultValue={formik.values.reviewers || []}
                  readOnly={isReadOnly}
                  placeholder={t("select").toString()}
                  onChange={(newValue) => {
                    formik.setFieldValue("reviewers", newValue);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.reviewers && formik.errors.reviewers}
                  disabled={formik.values.dontNeedReview}
                />
              </FormControl>
              {model && !model?.mandatoryReview && (
                <FormControlLabel
                  control={
                    <Checkbox
                      id="dontNeedReview"
                      checked={formik.values.dontNeedReview}
                      disabled={model?.isCompleted || isReadOnly}
                      onChange={(event: ChangeEvent<any>, checked: boolean) => {
                        formik.setFieldValue("dontNeedReview", checked);

                        if (checked) {
                          setTimeout(() => {
                            formik.setFieldValue("reviewers", []);
                          }, 100);
                        }
                      }}
                      onBlur={formik.handleBlur}
                    />
                  }
                  label={t("dontNeedReview")}
                />
              )}
              <FileUploader
                onChange={(file: File | File[] | FileWrapper[]) => {
                  setUploadDocument(file as File);
                }}
                allowedExtensions={[".doc", ".docx"]}
                isReadOnly={isReadOnly}
                errorValidate={requiredDocumentError}
              />
            </FormGroup>
          </div>
          {model?.draft && (
            <div className={styles["item-2"]}>
              <DocumentView document={model.draft} />
            </div>
          )}
        </div>
        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>
          <Button
            className={styles.save}
            disabled={isReadOnly}
            onClick={() =>
              validateBeforeExecute(formik, () => save(formik.values, false))
            }
          >
            {t("save")}
          </Button>
          <Button
            className={styles.continue}
            disabled={isReadOnly}
            onClick={() =>
              validateBeforeExecute(
                formik,
                () => save(formik.values, true),
                handleContinueCondition
              )
            }
          >
            {t("continue")}
          </Button>
        </div>
      </form>
      <SaveSuccessDialog open={saveSuccess} />
      <CancelDialog
        open={cancelDialogShow}
        yesAction={() => navigate(-1)}
        noAction={() => cancelDialogShowSet(false)}
      />
    </SpinnerArea>
  );
};

export default DraftForFeedback;
